/**
 * Both ends of the layout worker conversation (ADR 3): the worker side parses each arriving request, handles
 * it and answers with its ID; the main side numbers its requests, parses every answer and settles the request
 * it names. The message ports are injected, so both run in tests over a `MessageChannel`. Table W in the part
 * 12 log.
 */
import type { TextChange } from '@cockatrice/core';
import type { FaceKey } from '../font/faces.ts';
import type { GlyphRun, ShapeOptions } from '../font/shape.ts';
import { messageId, parseLayoutRequest, parseLayoutResponse, ProtocolError } from './protocol.ts';
import type { BookSlot, HeldBookDraw, LayoutRequest, LayoutResponse, LoadedFace, PreviewSettings, ShelfChapter } from './protocol.ts';
import type { PdfBook } from '../book/pages.ts';
import type { BookMode, Place } from '../book/spreads.ts';

/**
 * A response and what to transfer with it: glyph runs' buffers, and drawn pages' bitmaps of type `B` (the worker's
 * `ImageBitmap`; none where nothing is drawn).
 */
export interface Reply<B extends object = never> {
  readonly message: LayoutResponse<B>;
  readonly transfer: readonly (ArrayBuffer | B)[];
}

/** The message event field both ends read. */
export interface MessageLike {
  readonly data: unknown;
}

/** The worker's global scope (or any port), as far as serving goes. */
export interface WorkerScope<B extends object = never> {
  addEventListener(type: 'message', listener: (event: MessageLike) => void): void;
  postMessage(message: unknown, transfer: (ArrayBuffer | B)[]): void;
}

/** A thrown value as an error message: the error's name and message, or the value as text. */
export function describeError(error: unknown): string {
  return error instanceof Error ? `${error.name}: ${error.message}` : String(error);
}

function errorReply(id: number, error: unknown): Reply {
  return { message: { id, kind: 'error', message: describeError(error) }, transfer: [] };
}

/** Sends a message about a request before its answer (E3: an export's progress); it carries nothing to transfer. */
export type Notify = (message: LayoutResponse<never>) => void;

/**
 * Serves layout requests on a worker scope. Each request is handled as it arrives (W1) and answered with its
 * ID once done, so a shape waiting on a face's load doesn't hold up another. A request that fails to parse is
 * answered with an error when it carries a valid ID (W2) and dropped when it doesn't (W3); a handler that
 * throws is answered with an error (W4); a reply that can't be posted is replaced by an error reply (W5). A handler may
 * send messages about its request before answering it (W14).
 */
export function serve<B extends object = never>(scope: WorkerScope<B>, handle: (request: LayoutRequest, notify: Notify) => Promise<Reply<B>>): void {
  const post = (reply: Reply<B>): void => {
    try {
      scope.postMessage(reply.message, [...reply.transfer]);
    } catch (error) {
      scope.postMessage(errorReply(reply.message.id, error).message, []);
    }
  };
  scope.addEventListener('message', (event) => {
    let request: LayoutRequest;
    try {
      request = parseLayoutRequest(event.data);
    } catch (error) {
      const id = messageId(event.data);
      if (id !== null) post(errorReply(id, error));
      return;
    }
    handle(request, (message) => {
      post({ message, transfer: [] });
    }).then(post, (error: unknown) => {
      post(errorReply(request.id, error));
    });
  });
}

/** The layout worker, seen from the main thread. */
export interface WorkerPort {
  addEventListener(type: 'message' | 'messageerror' | 'error', listener: (event: unknown) => void): void;
  postMessage(message: unknown, transfer: ArrayBuffer[]): void;
}

/** A request the worker answered with an error, or a worker that failed or stopped answering. */
export class LayoutWorkerError extends Error {
  override readonly name = 'LayoutWorkerError';
}

/** Starts a timer and returns the function that cancels it; the app passes one built on `setTimeout`. */
export type StartTimer = (callback: () => void, ms: number) => () => void;

/** How long the worker may take to answer one request. */
export interface ChannelTimeout {
  readonly ms: number;
  readonly start: StartTimer;
}

/** What a spread request asks (V11; part 15a, tables T and Q): the window around a place, or around the cursor. */
export interface SpreadAsk {
  /** The session the cursor's chapter is open under. */
  readonly session: number;
  readonly cursor: number;
  /** The place to turn from, or null to follow the cursor (T4). */
  readonly place: Place | null;
  readonly turn: number;
  readonly mode: BookMode;
  /** How many chapters the book has. */
  readonly chapters: number;
  readonly scale: number;
  readonly held: readonly HeldBookDraw[];
  /** A page drawn apart from the view: what the view holds stays recorded (part 15b, CU13). */
  readonly apart?: true;
}

/** The book view's window as the main thread receives it (V12; part 15a, U5, T6, Q18). */
export interface BookView {
  readonly units: readonly (readonly BookSlot[])[];
  readonly current: number;
  readonly place: Place | null;
  readonly turned: number;
  readonly start: boolean;
  readonly end: boolean;
  /** A settle would lay out more (Q18). */
  readonly unsettled: boolean;
}

/** An export under way (E): its file, or null when it was cancelled; and what cancels it. */
export interface PdfExport {
  readonly result: Promise<Uint8Array<ArrayBuffer> | null>;
  /** Asks the worker to stop the export; it then answers `result` with null, unless it finished first. */
  cancel(): Promise<void>;
}

/** The layout worker's requests, as promises. */
export interface LayoutClient {
  /** Loads faces (or finds them loaded) and gives their metrics, in the order asked. */
  load(faces: readonly FaceKey[]): Promise<readonly LoadedFace[]>;
  /** Shapes text with a face, loading it first if needed. */
  shape(face: FaceKey, text: string, options: ShapeOptions): Promise<GlyphRun>;
  /** Q1–Q3: opens `chapter` (the main thread's number for this session) with its text and settings. */
  open(chapter: number, text: string, settings: PreviewSettings): Promise<void>;
  /** Q4, Q5: applies an edit to the open chapter; false when `chapter` is no longer open (stale). */
  edit(chapter: number, changes: readonly TextChange[]): Promise<boolean>;
  /** Q9: lays the chapter out to its end; true when complete, false when interrupted, null when stale. */
  settle(chapter: number): Promise<boolean | null>;
  /** Q10: closes the chapter's session. */
  close(chapter: number): Promise<void>;
  /** Q20, Q21: sets the chapters the book view lays out beside `session`'s; false when the session is no longer open. */
  shelf(session: number, chapters: readonly ShelfChapter[]): Promise<boolean>;
  /** Q13–Q18: the book view's window; null when out of date (stale). */
  spread(ask: SpreadAsk): Promise<BookView | null>;
  /** E: exports `book` as a PDF, telling `progress` how far it has come (in units of the book's text). */
  pdf(book: PdfBook, progress: (done: number, total: number) => void): PdfExport;
  /** W15: the worker was stopped: every request still waiting rejects, and none is sent after. */
  stop(): void;
}


interface Pending {
  readonly settle: (response: LayoutResponse) => void;
  readonly reject: (error: unknown) => void;
  /** W14: a message about the request before its answer, or undefined when the request takes none. */
  readonly progress?: ((response: Extract<LayoutResponse, { kind: 'progress' }>) => void) | undefined;
  cancel: () => void;
}

function eventData(event: unknown): unknown {
  return typeof event === 'object' && event !== null && 'data' in event ? event.data : undefined;
}

function eventMessage(event: unknown): string {
  if (typeof event === 'object' && event !== null && 'message' in event && typeof event.message === 'string') return event.message;
  return 'no details';
}

function mismatch(asked: string, response: LayoutResponse): ProtocolError {
  return new ProtocolError(`a ${asked} request was answered with "${response.kind}"`);
}

/**
 * Connects to the layout worker. Each request gets the next ID (W6). An answer settles the request it names:
 * an error answer rejects it with a {@link LayoutWorkerError} (W7), an answer of the wrong kind with a
 * {@link ProtocolError} (W8); an answer that fails to parse rejects the request its ID names, if any (W9); an
 * answer naming no pending request is dropped (W10). A worker `error` or `messageerror` event rejects every
 * pending request (W11). With a timeout, a request unanswered in time rejects and its late answer is dropped
 * (W12). A request that can't be posted rejects at once (W13). A progress message goes to the request it names when
 * that request takes progress, and restarts its timeout; to any other it is an answer of the wrong kind (W14). Once
 * stopped, every pending request rejects and every later one at once (W15).
 */
export function connect(port: WorkerPort, timeout?: ChannelTimeout): LayoutClient {
  const pending = new Map<number, Pending>();
  let nextId = 0;
  let stopped = false;
  const take = (id: number): Pending | undefined => {
    const entry = pending.get(id);
    pending.delete(id);
    entry?.cancel();
    return entry;
  };
  const failAll = (error: LayoutWorkerError): void => {
    const entries = [...pending.values()];
    pending.clear();
    for (const entry of entries) {
      entry.cancel();
      entry.reject(error);
    }
  };
  port.addEventListener('message', (event) => {
    const data = eventData(event);
    let response: LayoutResponse;
    try {
      response = parseLayoutResponse(data);
    } catch (error) {
      const id = messageId(data);
      if (id !== null) take(id)?.reject(error);
      return;
    }
    if (response.kind === 'progress') {
      const waiting = pending.get(response.id);
      if (waiting?.progress === undefined) take(response.id)?.reject(new ProtocolError(`request ${response.id} takes no progress`));
      else {
        waiting.cancel();
        startTimer(response.id, waiting);
        waiting.progress(response);
      }
      return;
    }
    const entry = take(response.id);
    if (entry === undefined) return;
    if (response.kind === 'error') entry.reject(new LayoutWorkerError(response.message));
    else entry.settle(response);
  });
  port.addEventListener('messageerror', () => {
    failAll(new LayoutWorkerError('the worker sent a message that could not be read'));
  });
  port.addEventListener('error', (event) => {
    failAll(new LayoutWorkerError(`the worker failed: ${eventMessage(event)}`));
  });
  /** W12: the request's timer, when there is a timeout. */
  const startTimer = (id: number, entry: Pending): void => {
    if (timeout === undefined) return;
    entry.cancel = timeout.start(() => {
      take(id)?.reject(new LayoutWorkerError(`the worker did not answer within ${timeout.ms} ms`));
    }, timeout.ms);
  };
  const send = (message: LayoutRequest, entry: Pending): void => {
    if (stopped) {
      entry.reject(new LayoutWorkerError('the worker was stopped'));
      return;
    }
    pending.set(message.id, entry);
    try {
      port.postMessage(message, []);
    } catch (error) {
      pending.delete(message.id);
      entry.reject(error);
      return;
    }
    startTimer(message.id, entry);
  };
  const id = (): number => {
    nextId += 1;
    return nextId - 1;
  };
  /** Sends `message`; `read` gives the value an answer resolves it with, or null for any other answer, which rejects it (W8). */
  const ask = <T>(message: LayoutRequest, read: (response: LayoutResponse) => { readonly value: T } | null, progress?: Pending['progress']): Promise<T> =>
    new Promise((resolve, reject) => {
      const settle = (response: LayoutResponse): void => {
        const answer = read(response);
        if (answer === null) reject(mismatch(message.kind, response));
        else resolve(answer.value);
      };
      send(message, { settle, reject, progress, cancel: () => undefined });
    });
  return {
    load: (faces) => ask({ id: id(), kind: 'load', faces: [...faces] }, (response) => (response.kind === 'loaded' ? { value: response.faces } : null)),
    shape: (face, text, options) =>
      ask({ id: id(), kind: 'shape', face, text, smallCaps: options.smallCaps, language: options.language }, (response) => (response.kind === 'shaped' ? { value: response.run } : null)),
    open: (chapter, text, settings) => ask({ id: id(), kind: 'open', chapter, text, settings }, (response) => (response.kind === 'done' ? { value: undefined } : null)),
    edit: (chapter, changes) =>
      ask({ id: id(), kind: 'edit', chapter, changes: [...changes] }, (response) => {
        if (response.kind === 'done') return { value: true };
        return response.kind === 'stale' ? { value: false } : null;
      }),
    settle: (chapter) =>
      ask<boolean | null>({ id: id(), kind: 'settle', chapter }, (response) => {
        if (response.kind === 'settled') return { value: response.complete };
        return response.kind === 'stale' ? { value: null } : null;
      }),
    close: (chapter) => ask({ id: id(), kind: 'close', chapter }, (response) => (response.kind === 'done' ? { value: undefined } : null)),
    shelf: (session, chapters) =>
      ask({ id: id(), kind: 'shelf', session, chapters: [...chapters] }, (response) => {
        if (response.kind === 'done') return { value: true };
        return response.kind === 'stale' ? { value: false } : null;
      }),
    spread: ({ session, cursor, place, turn, mode, chapters, scale, held, apart }) =>
      ask<BookView | null>({ id: id(), kind: 'spread', session, cursor, place, turn, mode, chapters, scale, held: [...held], ...(apart === true ? { apart } : {}) }, (response) => {
        if (response.kind === 'spread') {
          const { units, current, place, turned, start, end, unsettled } = response;
          return { value: { units, current, place, turned, start, end, unsettled } };
        }
        return response.kind === 'stale' ? { value: null } : null;
      }),
    pdf: (book, progress) => {
      const job = id();
      const result = ask<Uint8Array<ArrayBuffer> | null>(
        { id: job, kind: 'pdf', book },
        (response) => {
          if (response.kind === 'pdf') return { value: new Uint8Array(response.bytes) };
          return response.kind === 'cancelled' ? { value: null } : null;
        },
        (response) => {
          progress(response.done, response.total);
        },
      );
      return {
        result,
        cancel: () => ask({ id: id(), kind: 'cancel', job }, (response) => (response.kind === 'done' ? { value: undefined } : null)),
      };
    },
    stop: () => {
      stopped = true;
      failAll(new LayoutWorkerError('the worker was stopped'));
    },
  };
}
